import { useFhirData } from "@/utils/useFhirData";
import {
  documentBelongsToEncounter,
  extractBasedOnResourceIds,
  isDocumentSigned,
} from "./editHydrationHelpers";

export type EncounterLinkedDocument = {
  documentReferenceId: string;
  type: string;
  description: string;
  binaryUrl: string;
  category?: string;
  basedOnIds: string[];
  signed: boolean;
};

function mapDocEntry(el: any): EncounterLinkedDocument | null {
  const resource = el?.resource;
  if (resource?.resourceType !== "DocumentReference") return null;

  return {
    documentReferenceId: resource?.id,
    type: resource?.type?.coding?.[0]?.display || "Documento",
    description:
      resource?.description ||
      resource?.type?.coding?.[0]?.display ||
      "Documento",
    binaryUrl: resource?.content?.[0]?.attachment?.url || "",
    category: resource?.category?.[0]?.coding?.[0]?.code,
    basedOnIds: extractBasedOnResourceIds(resource),
    signed: isDocumentSigned(resource),
  };
}

/**
 * DocumentReference do encounter (assinados e pendentes),
 * dos bundles prescricao / service-request / atestado do Patient Summary.
 */
export function collectEncounterDocuments(
  summaryData: any,
  encounterId: string
): EncounterLinkedDocument[] {
  if (!summaryData || !encounterId) return [];

  const categories = ["prescricao", "service-request", "atestado"];
  const docs: EncounterLinkedDocument[] = [];

  categories.forEach((descriptor) => {
    const bundle = useFhirData.extractPatientSummaryResource(
      summaryData,
      "DocumentReference",
      descriptor
    )?.[0];

    bundle?.entry?.forEach((el: any) => {
      if (!documentBelongsToEncounter(el?.resource, encounterId)) return;
      const mapped = mapDocEntry(el);
      if (mapped) docs.push(mapped);
    });
  });

  return docs;
}

/**
 * Apenas DocumentReference assinados (ProofOfOrigin) — UI "Registros assinados".
 */
export function collectSignedDocumentsForEncounter(
  summaryData: any,
  encounterId: string
): EncounterLinkedDocument[] {
  return collectEncounterDocuments(summaryData, encounterId).filter(
    (d) => d.signed
  );
}

/** @deprecated alias — prefer EncounterLinkedDocument */
export type EncounterSignedDocument = EncounterLinkedDocument;

/**
 * Anexa documentReferenceId / sign / binaryUrl às intervenções quando
 * houver DocumentReference (assinado ou pendente) ligado via basedOn.
 *
 * - Assinado → sign + documentReferenceId (não re-posta PDF)
 * - Pendente com basedOn → documentReferenceId + skipDocumentPost (não duplica)
 * - Sem match (órfão) → permanece !sign / sem documentReferenceId
 *   → Footer sempre posta DocumentReference no finish (basedOn nos ids existentes/novos)
 *
 * Obs.: skipDocumentPost / documentReferenceId de doc pendente NÃO impedem
 * novo DocumentReference na finalização (regra: sempre postar se !sign).
 */
export function enrichInterventionsWithSignedDocuments(
  interventions: any[],
  encounterDocuments: EncounterLinkedDocument[]
): any[] {
  if (!interventions?.length) return interventions || [];

  return interventions.map((item: any) => {
    // Atestado: o próprio DocumentReference é a intervenção
    if (item?.type === "certificate" && item?.documentReferenceId) {
      const certDoc = encounterDocuments.find(
        (d) => d.documentReferenceId === item.documentReferenceId
      );
      if (certDoc?.signed) {
        return {
          ...item,
          sign: true,
          binaryUrl: certDoc.binaryUrl || item.binaryUrl,
          documentReferenceId: certDoc.documentReferenceId,
        };
      }
      // Doc existe mas sem ProofOfOrigin: ainda evita re-post do PDF
      return { ...item, skipDocumentPost: true };
    }

    const resourceId = item?.medicationRequestId || item?.serviceRequestId;
    if (!resourceId) return item;

    const linkedSigned = encounterDocuments.find(
      (d) => d.signed && d.basedOnIds?.some((id) => id === resourceId)
    );
    if (linkedSigned) {
      return {
        ...item,
        sign: true,
        documentReferenceId: linkedSigned.documentReferenceId,
        binaryUrl: linkedSigned.binaryUrl,
        signedDocumentType: linkedSigned.type,
      };
    }

    const linkedPending = encounterDocuments.find(
      (d) => !d.signed && d.basedOnIds?.some((id) => id === resourceId)
    );
    if (linkedPending) {
      // Já há DocumentReference pendente com basedOn — não duplicar no finish
      return {
        ...item,
        documentReferenceId: linkedPending.documentReferenceId,
        binaryUrl: linkedPending.binaryUrl,
        skipDocumentPost: true,
      };
    }

    // Órfão: MedicationRequest/ServiceRequest sem DocumentReference correspondente
    return item;
  });
}
